import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  MapPin, Clock, CheckCircle2, CreditCard, Loader2, Package, Tag, User, Baby,
  AlertCircle, ChevronLeft, ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import CircuitDivider from '../components/CircuitDivider';
import { BTN_OUTLINE, BTN_PRIMARY } from '../config/brand';
import { useSettings } from '../context/SettingsContext';
import { formulaMatchesDuration, slotDateLabel, slotOptionKey } from '../lib/slotDates';
import { firstWeekday, slotDaysLabel } from '../lib/weekdays';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DAY_ORDER = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const LEAD_STORAGE_KEY = 'digirobot_lead_id';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[\d\s+().-]{8,}$/;

const WORKSHOP_TO_OFFER_SLUG = {
  decouverte: 'atelier-decouverte',
  defi: 'atelier-defi',
  particulier: 'atelier-particulier',
};

function resolveOfferSlug(slug) {
  if (!slug) return slug;
  return WORKSHOP_TO_OFFER_SLUG[slug] || slug;
}

function bookingTypeFromCategory(category) {
  if (category === 'cursus') return 'cursus';
  if (category === 'stage') return 'stage';
  return 'atelier';
}

function bookingTypeFromParam(type) {
  if (type === 'cursus') return 'cursus';
  if (type === 'stage') return 'stage';
  if (type === 'atelier') return 'atelier';
  return null;
}

const CATEGORY_SORT = { atelier: 0, cursus: 1, stage: 2 };

function isBookableOffer(offer) {
  return offer?.active !== false && (offer.formulas || []).length > 0;
}

function isFreeOffer(offer) {
  const formulas = offer.formulas || [];
  if (formulas.length === 0) return false;
  return formulas.every((f) => Number(f.price) === 0);
}

function offerMinPrice(offer) {
  const prices = (offer.formulas || [])
    .map((f) => Number(f.price))
    .filter((n) => !Number.isNaN(n));
  return prices.length ? Math.min(...prices) : null;
}

function offerPriceLabel(offer) {
  const min = offerMinPrice(offer);
  if (min == null) return '';
  if (min === 0) return 'Offert';
  const formulas = offer.formulas || [];
  const hasRange = formulas.some((f) => Number(f.price) > min);
  return hasRange ? `Dès ${min} €` : `${min} €`;
}

function durationTokenForFormula(formula) {
  if (!formula) return '';
  const kind = String(formula.duration_kind || '').toLowerCase();
  if (kind === 'half_day') return 'demi-journee';
  if (kind === 'full_day') return 'journee';
  if (kind === 'full_week') return 'semaine';
  const duration = String(formula.duration || '').toLowerCase();
  if (duration === '10h' || duration.includes('demi')) return 'demi-journee';
  if (duration === '20h' || duration.includes('semaine')) return 'semaine';
  if (duration.includes('journee') || duration.includes('journée')) return 'journee';
  const name = String(formula.name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  if (name.includes('demi')) return 'demi-journee';
  if (name.includes('semaine')) return 'semaine';
  if (name.includes('journee')) return 'journee';
  return formula.id || '';
}

function categoryLabel(category) {
  if (category === 'cursus') return 'Cursus';
  if (category === 'stage') return 'Stage';
  return 'Atelier';
}

function sortBookableOffers(list) {
  return [...list].sort((a, b) => {
    const ca = CATEGORY_SORT[a.category] ?? 9;
    const cb = CATEGORY_SORT[b.category] ?? 9;
    if (ca !== cb) return ca - cb;
    const fa = isFreeOffer(a) ? 1 : 0;
    const fb = isFreeOffer(b) ? 1 : 0;
    if (fa !== fb) return fa - fb;
    return (a.display_order ?? 99) - (b.display_order ?? 99);
  });
}

function validateEmail(email) {
  if (!email?.trim()) return 'L\'email est requis';
  if (!EMAIL_RE.test(email.trim())) return 'Format d\'email invalide';
  return '';
}

function validatePhone(phone) {
  if (!phone?.trim()) return 'Le téléphone est requis';
  if (!PHONE_RE.test(phone.trim())) return 'Numéro de téléphone invalide';
  return '';
}

function validateRequired(value, label) {
  if (!value?.trim()) return `${label} est requis`;
  return '';
}

function validateChildAge(age) {
  if (!age && age !== 0) return 'L\'âge est requis';
  const n = parseInt(age, 10);
  if (Number.isNaN(n) || n < 6 || n > 16) return 'L\'âge doit être entre 6 et 16 ans';
  return '';
}

function isStageSlot(schedule, offer) {
  return offer?.category === 'stage' || schedule?.offer_category === 'stage';
}

function isValidSlot(schedule, offer) {
  if (!schedule?.id || schedule.closed || !(Number(schedule.available_spots) > 0)) return false;
  if (isStageSlot(schedule, offer)) {
    // Date calendaire requise ; stage_id thématique optionnel (offre Stage Vacances).
    return Boolean(schedule.start_date);
  }
  return true;
}

function isAppointmentOffer(offer) {
  return offer?.slug === 'atelier-particulier';
}

function hasBookingDeepLink(params) {
  return Boolean(
    params.get('offer') ||
    params.get('id') ||
    params.get('duration') ||
    params.get('formula') ||
    params.get('schedule') ||
    params.get('center')
  );
}

/** Premier step encore incomplet (0 Email … 6 Paiement). */
function firstIncompleteStepIndex({
  hasLead,
  offer,
  formula,
  centerId,
  schedule,
  infoComplete,
  centerStepEnabled = true,
  appointmentOffer = false,
}) {
  if (!hasLead) return 0;
  if (!offer) return 1;
  if (!formula) return 2;
  if (appointmentOffer) {
    if (!infoComplete) return 5;
    return 6;
  }
  if (centerStepEnabled && !centerId) return 3;
  if (!isValidSlot(schedule, offer)) return 4;
  if (!infoComplete) return 5;
  return 6;
}

function clampBookingStep(target, ctx) {
  const max = firstIncompleteStepIndex(ctx);
  const n = Number(target);
  if (!Number.isFinite(n)) return max;
  let next = Math.max(0, Math.min(Math.trunc(n), max));
  if (ctx.appointmentOffer && (next === 3 || next === 4)) {
    next = ctx.formula ? Math.min(5, max) : 2;
  }
  if (ctx.centerStepEnabled === false && next === 3) {
    next = ctx.formula ? Math.min(4, max) : 2;
  }
  return next;
}

async function readApiError(res, fallback) {
  try {
    const data = await res.json();
    return data.detail || data.error || fallback;
  } catch {
    return fallback;
  }
}

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p className="text-red-600 text-xs mt-1.5 flex items-center gap-1.5" role="alert">
      <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {message}
    </p>
  );
}

function StepPanel({ stepKey, children }) {
  return (
    <div
      key={stepKey}
      id="booking-step-panel"
      tabIndex={-1}
      className="animate-in fade-in slide-in-from-right-3 duration-300 outline-none"
    >
      {children}
    </div>
  );
}

function LoadingBlock({ message = 'Chargement…' }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 sm:py-16 text-slate-600" role="status" aria-live="polite">
      <Loader2 className="h-9 w-9 text-[#3a00e0] animate-spin mb-3" aria-hidden />
      <p className="text-sm sm:text-base">{message}</p>
    </div>
  );
}

function EmptyStep({ message, hint, onBack, backLabel = 'Retour' }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(250,176,58,0.12)]">
        <AlertCircle className="h-6 w-6 text-[#f97316]" aria-hidden />
      </div>
      <p className="text-slate-800 font-medium">{message}</p>
      {hint && <p className="mt-2 text-sm text-slate-500 max-w-sm mx-auto">{hint}</p>}
      {onBack && (
        <Button variant="outline" className={`mt-6 ${BTN_OUTLINE}`} onClick={onBack}>
          <ChevronLeft className="h-4 w-4 mr-1" />{backLabel}
        </Button>
      )}
    </section>
  );
}

function StepShell({ title, description, icon: Icon, children, footer }) {
  return (
    <section className="rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden">
      <header className="border-b border-slate-100 px-5 sm:px-7 py-5 sm:py-6">
        <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2.5">
          {Icon && (
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[rgba(58,0,224,0.08)]">
              <Icon className="h-4 w-4 text-[#3a00e0]" aria-hidden />
            </span>
          )}
          {title}
        </h2>
        {description && (
          <div className="mt-1.5 text-sm sm:text-base text-slate-600 leading-relaxed">{description}</div>
        )}
      </header>
      <div className="px-5 sm:px-7 py-5 sm:py-6">{children}</div>
      {footer && (
        <footer className="border-t border-slate-100 px-5 sm:px-7 py-4 bg-slate-50/60">
          {footer}
        </footer>
      )}
    </section>
  );
}

const Reserver = () => {
  const location = useLocation();
  const { toast } = useToast();
  const { settings } = useSettings();
  const centerStepEnabled = settings.booking_center_step_enabled === true;
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const typeParam = params.get('type');
  const prefilledFormulaRef = useRef(false);
  const hydratedBookingRef = useRef(false);
  const centerPrefillDone = useRef(false);
  const schedulePrefillDone = useRef(false);
  const formAnchorRef = useRef(null);

  const [step, setStep] = useState(() => (sessionStorage.getItem(LEAD_STORAGE_KEY) ? 1 : 0));
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);
  const [offersLoading, setOffersLoading] = useState(true);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [schedulesReady, setSchedulesReady] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(!!sessionStorage.getItem(LEAD_STORAGE_KEY));

  const [offers, setOffers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [altFormulas, setAltFormulas] = useState([]);
  const [altFormulasLoading, setAltFormulasLoading] = useState(false);

  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectedFormula, setSelectedFormula] = useState(null);
  const [selectedCenterId, setSelectedCenterId] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [skippedFormulaStep, setSkippedFormulaStep] = useState(false);

  const [formData, setFormData] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    child_name: '',
    child_age: '',
  });

  const [booking, setBooking] = useState(null);
  const [promoInput, setPromoInput] = useState('');
  const [promoInfo, setPromoInfo] = useState(null);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadName, setLeadName] = useState('');
  const [leadTouched, setLeadTouched] = useState({});
  const [infoTouched, setInfoTouched] = useState({});
  const [unlockEmail, setUnlockEmail] = useState(false);

  const leadEmailError = validateEmail(leadEmail);
  const isLeadValid = !leadEmailError;

  const infoErrors = useMemo(() => ({
    parent_name: validateRequired(formData.parent_name, 'Le nom'),
    parent_email: validateEmail(formData.parent_email),
    parent_phone: validatePhone(formData.parent_phone),
    child_name: validateRequired(formData.child_name, 'Le prénom'),
    child_age: validateChildAge(formData.child_age),
  }), [formData]);

  const isInfoValid = Object.values(infoErrors).every((e) => !e);

  const tunnelRef = useRef({});
  tunnelRef.current = {
    hasLead: Boolean(booking?.id || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(LEAD_STORAGE_KEY))),
    offer: selectedOffer,
    formula: selectedFormula,
    centerId: selectedCenterId,
    schedule: selectedSchedule,
    infoComplete: isInfoValid,
    centerStepEnabled,
    appointmentOffer: isAppointmentOffer(selectedOffer),
  };

  const goToStep = useCallback((next, overrides = {}) => {
    const safe = clampBookingStep(next, { ...tunnelRef.current, ...overrides });
    setStep(safe);
    // Sous le header fixed (banner + navbar), pas jusqu'à y=0
    requestAnimationFrame(() => {
      formAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const panel = document.getElementById('booking-step-panel');
      panel?.focus({ preventScroll: true });
    });
  }, []);

  const patchBooking = async (bookingId, payload) => {
    const res = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Mise à jour impossible'));
    return res.json();
  };

  const filteredOffers = useMemo(() => {
    const bookable = offers.filter(isBookableOffer);
    if (!typeParam) return sortBookableOffers(bookable);
    const cat = bookingTypeFromParam(typeParam);
    const filtered = bookable.filter((o) => o.category === cat);
    return sortBookableOffers(filtered.length > 0 ? filtered : bookable);
  }, [offers, typeParam]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/payment/config`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setPaymentsEnabled(Boolean(data?.payments_enabled));
      })
      .catch(() => {
        if (!cancelled) setPaymentsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Reprise du lead / panier abandonné
  useEffect(() => {
    const leadId = sessionStorage.getItem(LEAD_STORAGE_KEY);
    if (!leadId) {
      setResumeLoading(false);
      return;
    }

    let cancelled = false;
    const resume = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/bookings/${leadId}`);
        if (!res.ok) {
          sessionStorage.removeItem(LEAD_STORAGE_KEY);
          if (!cancelled) {
            setBooking(null);
            setStep(0);
          }
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        if (data.status === 'confirmed') {
          sessionStorage.removeItem(LEAD_STORAGE_KEY);
          setBooking(null);
          setStep(0);
          return;
        }
        setBooking(data);
        setLeadEmail(data.parent_email || '');
        setLeadName(data.parent_name || '');
        setFormData((prev) => ({
          ...prev,
          parent_name: data.parent_name || prev.parent_name,
          parent_email: data.parent_email || prev.parent_email,
          parent_phone: data.parent_phone || prev.parent_phone,
          child_name: data.child_name || prev.child_name,
          child_age: data.child_age != null ? String(data.child_age) : prev.child_age,
        }));
        if (data.promo_code) {
          setPromoInput(data.promo_code);
          setPromoInfo({
            code: data.promo_code,
            discount: data.discount,
          });
        }
      } catch {
        sessionStorage.removeItem(LEAD_STORAGE_KEY);
      } finally {
        if (!cancelled) setResumeLoading(false);
      }
    };
    resume();
    return () => { cancelled = true; };
  }, []);

  // Chargement offres + préremplissage query params
  useEffect(() => {
    const loadOffers = async () => {
      try {
        setOffersLoading(true);
        const res = await fetch(`${BACKEND_URL}/api/offers`);
        if (!res.ok) throw new Error('offers');
        const data = await res.json();
        if (!Array.isArray(data)) throw new Error('offers');
        setOffers(data);

        const idParam = params.get('id');
        const offerSlug = params.get('offer');
        const durationParam = (params.get('duration') || '').toLowerCase();
        const formulaParam = params.get('formula');
        let preOffer = null;

        if (offerSlug) {
          preOffer = data.find((o) => o.slug === resolveOfferSlug(offerSlug));
        }
        if (!preOffer && idParam) {
          preOffer = data.find(
            (o) => o.id === idParam || (o.formulas || []).some((f) => f.id === idParam)
          );
        }
        // type=atelier|cursus|stage filtre la liste, mais ne choisit pas d'offre.
        // Prefill uniquement via offer / id / formula / duration (fiches « Réserver cet atelier »).

        if (preOffer) {
          setSelectedOffer(preOffer);
          const formulas = preOffer.formulas || [];
          const byDuration = durationParam
            ? formulas.find((f) => formulaMatchesDuration(f, durationParam))
            : null;
          const preFormula =
            formulas.find((f) => f.id === idParam || f.id === formulaParam || f.id === durationParam) ||
            byDuration ||
            (formulas.length === 1 ? formulas[0] : null);

          const hasLead = !!sessionStorage.getItem(LEAD_STORAGE_KEY);
          if (preFormula) {
            setSelectedFormula(preFormula);
            setSkippedFormulaStep(formulas.length === 1 || !!byDuration || !!idParam || !!formulaParam);
            prefilledFormulaRef.current = true;
            setStep(hasLead ? (centerStepEnabled ? 3 : 4) : 0);
          } else {
            setStep(hasLead ? 2 : 0);
          }
        }
      } catch {
        toast({ title: 'Erreur', description: 'Impossible de charger les offres', variant: 'destructive' });
      } finally {
        setOffersLoading(false);
      }
    };
    loadOffers();
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hydrate offre / formule depuis le booking repris (si pas déjà prérempli par URL)
  useEffect(() => {
    if (hydratedBookingRef.current || !booking?.id || offers.length === 0 || offersLoading) return;
    if (hasBookingDeepLink(params)) {
      hydratedBookingRef.current = true;
      return;
    }
    if (!booking.offer_id && !booking.formula_id) {
      hydratedBookingRef.current = true;
      goToStep(1, { hasLead: true });
      return;
    }

    const offer =
      offers.find((o) => o.id === booking.offer_id) ||
      offers.find((o) => (o.formulas || []).some((f) => f.id === booking.formula_id));
    if (!offer) {
      hydratedBookingRef.current = true;
      goToStep(1, { hasLead: true });
      return;
    }

    setSelectedOffer(offer);
    const formula =
      (offer.formulas || []).find((f) => f.id === booking.formula_id) ||
      ((offer.formulas || []).length === 1 ? offer.formulas[0] : null);
    if (formula) {
      setSelectedFormula(formula);
      setSkippedFormulaStep((offer.formulas || []).length === 1);
      goToStep(isAppointmentOffer(offer) ? 5 : 3, {
        offer,
        formula,
        hasLead: true,
        appointmentOffer: isAppointmentOffer(offer),
      });
    } else {
      goToStep(2, { offer, hasLead: true });
    }
    hydratedBookingRef.current = true;
  }, [booking, offers, offersLoading, params, goToStep]);

  // Créneaux
  useEffect(() => {
    const loadSchedules = async () => {
      if (!selectedOffer || !selectedFormula) {
        setSchedulesReady(false);
        return;
      }
      if (isAppointmentOffer(selectedOffer)) {
        setSchedules([]);
        setSchedulesLoading(false);
        setSchedulesReady(true);
        return;
      }
      try {
        setSchedulesLoading(true);
        setSchedulesReady(false);
        let res = await fetch(
          `${BACKEND_URL}/api/schedules?offer_id=${selectedOffer.id}&formula_id=${selectedFormula.id}`
        );
        let data = await res.json();
        if (!Array.isArray(data)) data = [];

        // Repli : créneaux historiques sans formula_id (ateliers/cursus).
        // Stages : formule obligatoire — pas d'inclusion des slots sans formula_id.
        if (data.length === 0) {
          res = await fetch(`${BACKEND_URL}/api/schedules?offer_id=${selectedOffer.id}`);
          data = await res.json();
          if (!Array.isArray(data)) data = [];
          data = data.filter((s) => {
            if (s.formula_id === selectedFormula.id) return true;
            if (!s.formula_id && selectedOffer.category !== "stage") return true;
            return false;
          });
        }

        setSchedules(data);
      } catch {
        setSchedules([]);
        toast({ title: 'Erreur', description: 'Impossible de charger les créneaux', variant: 'destructive' });
      } finally {
        setSchedulesLoading(false);
        setSchedulesReady(true);
      }
    };
    loadSchedules();
  }, [selectedOffer, selectedFormula]); // eslint-disable-line react-hooks/exhaustive-deps

  const openSchedules = useMemo(
    () =>
      schedules.filter((s) => {
        if (s.closed) return false;
        if (!(Number(s.available_spots) > 0)) return false;
        if (isStageSlot(s, selectedOffer)) return Boolean(s.start_date);
        return true;
      }),
    [schedules, selectedOffer]
  );

  const eligibleCenters = useMemo(() => {
    const map = new Map();
    openSchedules.forEach((s) => {
      if (!map.has(s.center_id)) map.set(s.center_id, s.center_name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [openSchedules]);

  const centerSchedules = useMemo(() => {
    const rows = centerStepEnabled
      ? openSchedules.filter((s) => s.center_id === selectedCenterId)
      : openSchedules;
    return [...rows].sort((a, b) => {
      const center = String(a.center_name || "").localeCompare(String(b.center_name || ""), "fr");
      if (center) return center;
      const dateCmp = String(a.start_date || "").localeCompare(String(b.start_date || ""));
      if (dateCmp) return dateCmp;
      const day =
        DAY_ORDER.indexOf(firstWeekday(a.days_of_week || a.day_of_week) || (a.day_of_week || '').toLowerCase()) -
        DAY_ORDER.indexOf(firstWeekday(b.days_of_week || b.day_of_week) || (b.day_of_week || '').toLowerCase());
      if (day) return day;
      return String(a.start_time || "").localeCompare(String(b.start_time || ""));
    });
  }, [openSchedules, selectedCenterId, centerStepEnabled]);

  // Autres formules de la même offre qui ont des créneaux (si la formule courante est vide)
  useEffect(() => {
    let cancelled = false;
    const discover = async () => {
      if (!BACKEND_URL || !selectedOffer?.id || !selectedFormula?.id) {
        setAltFormulas([]);
        return;
      }
      if (!schedulesReady || schedulesLoading) return;
      if (openSchedules.length > 0) {
        setAltFormulas([]);
        setAltFormulasLoading(false);
        return;
      }
      const siblings = (selectedOffer.formulas || []).filter((f) => f.id !== selectedFormula.id);
      if (siblings.length === 0) {
        setAltFormulas([]);
        return;
      }
      setAltFormulasLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/schedules?offer_id=${selectedOffer.id}`);
        const data = await res.json();
        if (cancelled) return;
        if (!Array.isArray(data)) {
          setAltFormulas([]);
          return;
        }
        const withSlots = siblings.filter((f) =>
          data.some((s) => s.formula_id === f.id && isValidSlot(s, selectedOffer))
        );
        setAltFormulas(withSlots);
      } catch {
        if (!cancelled) setAltFormulas([]);
      } finally {
        if (!cancelled) setAltFormulasLoading(false);
      }
    };
    discover();
    return () => {
      cancelled = true;
    };
  }, [
    selectedOffer,
    selectedFormula,
    openSchedules.length,
    schedulesReady,
    schedulesLoading,
  ]);

  // Prefill centre / créneau (URL ou booking repris) — une seule fois
  useEffect(() => {
    if (isAppointmentOffer(selectedOffer)) return;
    if (schedulesLoading || !schedulesReady) return;

    const centerParam = params.get('center');
    const scheduleParam = params.get('schedule');
    const targetScheduleId = scheduleParam || booking?.schedule_id;
    const targetCenterId = centerParam || null;
    const deepLink = hasBookingDeepLink(params);
    const stepOverrides = {
      offer: selectedOffer,
      formula: selectedFormula,
      hasLead: Boolean(booking?.id),
    };

    if (targetScheduleId && !schedulePrefillDone.current) {
      const sessionStart = booking?.session_start;
      const sched =
        openSchedules.find(
          (s) => s.id === targetScheduleId && (!sessionStart || s.start_date === sessionStart)
        ) || openSchedules.find((s) => s.id === targetScheduleId);
      if (isValidSlot(sched, selectedOffer)) {
        setSelectedCenterId(sched.center_id);
        setSelectedSchedule(sched);
        schedulePrefillDone.current = true;
        centerPrefillDone.current = true;
        const slotCtx = { ...stepOverrides, schedule: sched, centerId: sched.center_id };
        const infoReady = Boolean(formData.parent_phone && formData.child_name && formData.parent_name);
        // /reserver nu : restaurer Enfant seulement si email+offre+formule+centre+slot sont valides. Jamais Paiement.
        if (!deepLink && booking?.schedule_id === sched.id) {
          goToStep(5, { ...slotCtx, infoComplete: infoReady });
        } else if (deepLink && booking?.schedule_id === sched.id && infoReady) {
          goToStep(booking.amount != null ? 6 : 5, { ...slotCtx, infoComplete: true });
        } else {
          goToStep(4, slotCtx);
        }
        return;
      }

      // Créneau persisté invalide (fermé, complet, disparu) — rester au centre / jour.
      schedulePrefillDone.current = true;
      const stale = schedules.find((s) => s.id === targetScheduleId);
      if (stale?.center_id) {
        setSelectedCenterId(stale.center_id);
        centerPrefillDone.current = true;
        goToStep(4, { ...stepOverrides, centerId: stale.center_id });
        return;
      }
    }

    if (targetCenterId && !centerPrefillDone.current) {
      const exists = eligibleCenters.some((c) => c.id === targetCenterId);
      if (exists) {
        setSelectedCenterId(targetCenterId);
        centerPrefillDone.current = true;
        goToStep(4, { ...stepOverrides, centerId: targetCenterId });
      }
    }
  }, [openSchedules, schedules, schedulesLoading, schedulesReady, params, booking, eligibleCenters, formData, selectedOffer, selectedFormula, goToStep]);

  // Garde : jamais Enfant/Paiement sans créneau valide (load + navigation).
  useEffect(() => {
    if (resumeLoading || offersLoading) return;
    const safe = clampBookingStep(step, tunnelRef.current);
    if (safe !== step) setStep(safe);
  }, [step, selectedOffer, selectedFormula, selectedCenterId, selectedSchedule, booking, isInfoValid, resumeLoading, offersLoading]);

  const handleSelectOffer = async (offer) => {
    setSelectedOffer(offer);
    setSelectedFormula(null);
    setSelectedCenterId(null);
    setSelectedSchedule(null);
    centerPrefillDone.current = false;
    schedulePrefillDone.current = false;

    const singleFormula = (offer.formulas || []).length === 1 ? offer.formulas[0] : null;
    const appointment = isAppointmentOffer(offer);
    if (singleFormula) {
      setSelectedFormula(singleFormula);
      setSkippedFormulaStep(true);
      goToStep(appointment ? 5 : (centerStepEnabled ? 3 : 4), {
        offer,
        formula: singleFormula,
        centerId: null,
        schedule: null,
        appointmentOffer: appointment,
      });
    } else {
      setSkippedFormulaStep(false);
      goToStep(2, { offer, formula: null, centerId: null, schedule: null, appointmentOffer: appointment });
    }

    if (booking?.id) {
      try {
        const payload = {
          offer_id: offer.id,
          booking_type: bookingTypeFromCategory(offer.category),
        };
        if (singleFormula) payload.formula_id = singleFormula.id;
        const data = await patchBooking(booking.id, payload);
        setBooking(data);
      } catch { /* non bloquant */ }
    }
  };

  const handleSelectFormula = async (formula) => {
    setSelectedFormula(formula);
    setSelectedCenterId(null);
    setSelectedSchedule(null);
    setSkippedFormulaStep(false);
    centerPrefillDone.current = false;
    schedulePrefillDone.current = false;
    const appointment = isAppointmentOffer(selectedOffer);
    // Met à jour duration/formula dans l’URL sans relancer le chargement des offres
    try {
      const nextParams = new URLSearchParams(window.location.search);
      const token = durationTokenForFormula(formula);
      if (token) nextParams.set('duration', token);
      if (formula?.id) nextParams.set('formula', formula.id);
      const qs = nextParams.toString();
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`
      );
    } catch { /* ignore */ }
    goToStep(appointment ? 5 : (centerStepEnabled ? 3 : 4), {
      formula,
      centerId: null,
      schedule: null,
      appointmentOffer: appointment,
    });
    if (booking?.id) {
      try {
        const data = await patchBooking(booking.id, {
          formula_id: formula.id,
          offer_id: selectedOffer?.id,
          booking_type: bookingTypeFromCategory(selectedOffer?.category),
        });
        setBooking(data);
      } catch { /* non bloquant */ }
    }
  };

  const handleSelectCenter = (centerId) => {
    setSelectedCenterId(centerId);
    setSelectedSchedule(null);
    goToStep(4, { centerId, schedule: null });
  };

  const handleSelectSchedule = async (schedule) => {
    if (!isValidSlot(schedule, selectedOffer)) {
      toast({
        title: schedule.closed ? 'Fermé' : schedule.available_spots <= 0 ? 'Complet' : 'Créneau indisponible',
        description: schedule.closed
          ? 'Ce créneau est temporairement fermé'
          : schedule.available_spots <= 0
            ? 'Ce créneau est complet'
            : "Cette session de stage n'est pas réservable",
        variant: 'destructive',
      });
      return;
    }
    setSelectedSchedule(schedule);
    goToStep(5, { schedule, centerId: schedule.center_id });
    if (booking?.id) {
      try {
        const data = await patchBooking(booking.id, {
          schedule_id: schedule.id,
          offer_id: selectedOffer?.id,
          formula_id: selectedFormula?.id,
          booking_type: bookingTypeFromCategory(selectedOffer?.category),
          session_start: schedule.start_date || null,
          session_end: schedule.end_date || schedule.start_date || null,
          stage_id: schedule.stage_id || null,
        });
        setBooking(data);
      } catch (error) {
        toast({ title: 'Créneau indisponible', description: error.message, variant: 'destructive' });
      }
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    setLeadTouched({ email: true });
    if (!isLeadValid) return;

    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_lead: true,
          parent_email: leadEmail.trim(),
          parent_name: leadName.trim() || null,
          booking_type: bookingTypeFromParam(typeParam) || bookingTypeFromCategory(selectedOffer?.category),
          offer_id: selectedOffer?.id || null,
          formula_id: selectedFormula?.id || null,
        }),
      });
      if (!res.ok) throw new Error(await readApiError(res, 'Erreur lors de la création du lead'));
      const data = await res.json();
      setBooking(data);
      sessionStorage.setItem(LEAD_STORAGE_KEY, data.id);
      setFormData((prev) => ({
        ...prev,
        parent_email: leadEmail.trim(),
        parent_name: leadName.trim() || prev.parent_name,
      }));
      const appointment = isAppointmentOffer(selectedOffer);
      goToStep(selectedOffer ? (selectedFormula ? (appointment ? 5 : (centerStepEnabled ? 3 : 4)) : 2) : 1, {
        hasLead: true,
        offer: selectedOffer,
        formula: selectedFormula,
        appointmentOffer: appointment,
      });
    } catch (error) {
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    setInfoTouched({
      parent_name: true,
      parent_email: true,
      parent_phone: true,
      child_name: true,
      child_age: true,
    });
    if (!isInfoValid) return;
    const appointment = isAppointmentOffer(selectedOffer);
    if (!appointment && !isValidSlot(selectedSchedule, selectedOffer)) {
      toast({ title: 'Créneau manquant', description: 'Veuillez choisir un créneau', variant: 'destructive' });
      goToStep(4);
      return;
    }

    try {
      setLoading(true);
      const payload = appointment
        ? {
            booking_type: bookingTypeFromCategory(selectedOffer.category),
            offer_id: selectedOffer.id,
            formula_id: selectedFormula.id,
            parent_name: formData.parent_name.trim(),
            parent_email: formData.parent_email.trim(),
            parent_phone: formData.parent_phone.trim(),
            child_name: formData.child_name.trim(),
            child_age: parseInt(formData.child_age, 10),
            appointment_request: true,
          }
        : {
            schedule_id: selectedSchedule.id,
            booking_type: bookingTypeFromCategory(selectedOffer.category),
            offer_id: selectedOffer.id,
            formula_id: selectedFormula.id,
            parent_name: formData.parent_name.trim(),
            parent_email: formData.parent_email.trim(),
            parent_phone: formData.parent_phone.trim(),
            child_name: formData.child_name.trim(),
            child_age: parseInt(formData.child_age, 10),
            session_start: selectedSchedule.start_date || null,
            session_end: selectedSchedule.end_date || selectedSchedule.start_date || null,
            stage_id: selectedSchedule.stage_id || null,
          };

      let data;
      if (booking?.id) {
        data = await patchBooking(booking.id, payload);
      } else {
        const res = await fetch(`${BACKEND_URL}/api/bookings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(await readApiError(res, 'Erreur lors de la création'));
        data = await res.json();
      }

      setBooking(data);
      sessionStorage.setItem(LEAD_STORAGE_KEY, data.id);
      if (appointment) sessionStorage.removeItem(LEAD_STORAGE_KEY);
      goToStep(6, {
        schedule: appointment ? null : selectedSchedule,
        hasLead: true,
        infoComplete: true,
        appointmentOffer: appointment,
      });
    } catch (error) {
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoInput.trim() || !booking) return;
    try {
      setPromoLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/bookings/${booking.id}/promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoInput.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.valid) {
        setPromoInfo(null);
        toast({
          title: 'Code invalide',
          description: data.error || data.detail || 'Code promo refusé',
          variant: 'destructive',
        });
        return;
      }
      setPromoInfo(data);
      setBooking({
        ...booking,
        amount: data.amount,
        discount: data.discount,
        promo_code: data.code,
        original_amount: booking.original_amount ?? selectedFormula?.price,
      });
      toast({ title: 'Code appliqué', description: `Réduction de ${data.discount} €` });
    } catch {
      toast({ title: 'Erreur', description: 'Validation impossible', variant: 'destructive' });
    } finally {
      setPromoLoading(false);
    }
  };

  const handleCheckout = async () => {
    try {
      setPaymentLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/bookings/${booking.id}/checkout`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.detail || 'Paiement impossible');
      window.location.href = data.url;
    } catch (error) {
      toast({
        title: paymentsEnabled ? 'Erreur de paiement' : 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
      setPaymentLoading(false);
    }
  };

  const backFromCity = () => {
    if (skippedFormulaStep || (selectedOffer?.formulas || []).length <= 1) {
      goToStep(1);
    } else {
      goToStep(2);
    }
  };

  const isCursus = selectedOffer?.category === 'cursus';
  const isStage = selectedOffer?.category === 'stage';
  const appointmentOffer = isAppointmentOffer(selectedOffer);
  const scheduleHint = isCursus
    ? 'Le créneau sera fixe pour toute l\'année — le lieu figure sur chaque ligne'
    : isStage
    ? 'Sélectionnez la session de stage qui vous convient — le centre est indiqué sur chaque créneau'
    : 'Chaque créneau affiche son centre. Choisissez le jour et le lieu qui vous conviennent.';
  const allStepDefs = [
    { key: 0, label: 'Email' },
    { key: 1, label: 'Activité' },
    { key: 2, label: 'Formule' },
    { key: 3, label: 'Centre' },
    { key: 4, label: 'Créneau' },
    { key: 5, label: 'Enfant' },
    { key: 6, label: appointmentOffer ? 'Demande' : (paymentsEnabled ? 'Paiement' : 'Récap') },
  ];
  const steps = allStepDefs.filter((s) => {
    if (!centerStepEnabled && s.key === 3) return false;
    if (appointmentOffer && (s.key === 3 || s.key === 4)) return false;
    return true;
  });
  const emailFromLead = Boolean(formData.parent_email?.trim() && !validateEmail(formData.parent_email));
  const showEmailField = !emailFromLead || unlockEmail;

  const isStepComplete = (index) => {
    if (index === 0) return Boolean(booking?.id || emailFromLead);
    if (index === 1) return Boolean(selectedOffer);
    if (index === 2) return Boolean(selectedFormula);
    if (index === 3) return appointmentOffer || !centerStepEnabled || Boolean(selectedCenterId);
    if (index === 4) return appointmentOffer || isValidSlot(selectedSchedule, selectedOffer);
    if (index === 5) {
      const slotOk = appointmentOffer || isValidSlot(selectedSchedule, selectedOffer);
      return slotOk && isInfoValid && Boolean(booking?.id) && step >= 6;
    }
    return false;
  };

  const canJumpToStep = (target) => {
    if (target >= step) return false;
    if (target === 3 && (!centerStepEnabled || appointmentOffer)) return false;
    if (target === 4 && appointmentOffer) return false;
    if (target === 2 && skippedFormulaStep && !selectedFormula) return false;
    if (target >= 5 && !appointmentOffer && !isValidSlot(selectedSchedule, selectedOffer)) return false;
    return true;
  };

  const jumpToStep = (target) => {
    if (!canJumpToStep(target)) return;
    goToStep(target);
  };
  const displayStep = Math.max(1, steps.findIndex((s) => s.key === step) + 1);
  const progressPct = Math.round((displayStep / steps.length) * 100);
  const currentStepLabel = steps.find((s) => s.key === step)?.label || '';

  const displayAmount = booking?.amount ?? selectedFormula?.price ?? 0;
  const displayOriginal = booking?.original_amount ?? selectedFormula?.price ?? displayAmount;
  const displayDiscount = booking?.discount || 0;

  const selectableCardClass =
    'cursor-pointer border-slate-200 bg-white hover:border-[rgba(58,0,224,0.4)] hover:bg-[rgba(58,0,224,0.03)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3a00e0]/40 focus-visible:border-[#3a00e0] transition-all duration-200 active:scale-[0.99]';
  const selectedCardClass = 'border-[#3a00e0] bg-[rgba(58,0,224,0.05)] ring-1 ring-[#3a00e0]/25';
  const inputClass =
    'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:border-[#3a00e0] focus-visible:ring-[#3a00e0]/25';
  const inputErrorClass = 'border-red-500/70 focus-visible:border-red-500 focus-visible:ring-red-500/20';

  const infoFieldClass = (field) =>
    `${inputClass} ${infoTouched[field] && infoErrors[field] ? inputErrorClass : ''}`;

  const backBtnClass = BTN_OUTLINE;

  if (resumeLoading && offersLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-white">
        <Navbar />
        <div className="pt-below-site-header pb-20 flex flex-col items-center justify-center text-slate-600">
          <LoadingBlock message="Chargement de votre réservation…" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-orange-50/30">
      <Navbar />

      {paymentLoading && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/45 backdrop-blur-sm flex items-center justify-center px-4"
          role="alertdialog"
          aria-busy="true"
          aria-label="Redirection vers le paiement"
        >
          <div className="w-full max-w-sm rounded-2xl bg-white border border-slate-200 shadow-xl px-6 py-8 text-center animate-in fade-in zoom-in-95 duration-300">
            <Loader2 className="h-11 w-11 text-[#3a00e0] animate-spin mx-auto mb-4" aria-hidden />
            <p className="text-slate-900 font-semibold text-lg">Redirection vers le paiement…</p>
            <p className="text-slate-500 text-sm mt-1.5">Ne fermez pas cette page</p>
          </div>
        </div>
      )}

      <div className="pt-below-site-header pb-28 sm:pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          <Reveal animation="fade-up" className="text-center mb-6 sm:mb-8">
            <p className="text-[#3a00e0] text-sm font-semibold uppercase tracking-wider mb-2">
              DigiRobot
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2 sm:mb-3 tracking-tight">
              Réserver <span className="text-gradient-brand">votre place</span>
            </h1>
            <p className="text-slate-600 text-base px-2 leading-relaxed max-w-xl mx-auto">
              Email, activité, créneau, puis paiement — vous pouvez revenir en arrière à tout moment.
            </p>
          </Reveal>

          <CircuitDivider className="mb-7 sm:mb-9" />

          <div ref={formAnchorRef} className="scroll-mt-site-header">
            {/* Mobile progress */}
            <div
              className="mb-5 lg:hidden sticky z-20 bg-white/95 backdrop-blur-md py-3 -mx-4 px-4 border-b border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
              style={{ top: 'var(--site-header-offset)' }}
              role="status"
              aria-label={`Étape ${displayStep} sur ${steps.length} : ${currentStepLabel}`}
            >
              <div className="flex items-center justify-between gap-3 text-sm mb-2" aria-current="step">
                <span className="text-[#3a00e0] font-semibold tabular-nums shrink-0">
                  Étape {displayStep}/{steps.length}
                </span>
                <span className="text-slate-800 font-medium truncate">{currentStepLabel}</span>
              </div>
              <div
                className="h-1.5 bg-slate-200 rounded-full overflow-hidden"
                role="progressbar"
                aria-valuemin={1}
                aria-valuemax={steps.length}
                aria-valuenow={displayStep}
                aria-label={`Progression : étape ${displayStep} sur ${steps.length}`}
              >
                <div
                  className="h-full bg-gradient-to-r from-[#3a00e0] to-[#fab03a] rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPct}%` }}
                  aria-hidden
                />
              </div>
              {step > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {steps.map((s) => (
                    s.key < step && isStepComplete(s.key) ? (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => jumpToStep(s.key)}
                        aria-label={`Revenir à ${s.label}`}
                        className="text-[11px] font-medium text-[#3a00e0] bg-[rgba(58,0,224,0.06)] hover:bg-[rgba(58,0,224,0.12)] rounded-full px-2 py-0.5 transition-colors"
                      >
                        {s.label}
                      </button>
                    ) : null
                  ))}
                </div>
              )}
            </div>

            {/* Desktop stepper */}
            <nav aria-label="Progression de la réservation" className="hidden lg:block mb-10">
              <ol className="flex items-start w-full">
                {steps.map((s, i) => {
                  const n = i + 1;
                  const done = isStepComplete(s.key);
                  const active = displayStep === n;
                  const clickable = canJumpToStep(s.key);
                  const StepInner = clickable ? 'button' : 'div';
                  return (
                    <li
                      key={s.key}
                      className={`flex items-start ${i < steps.length - 1 ? 'flex-1' : ''}`}
                      aria-current={active ? 'step' : undefined}
                    >
                      <StepInner
                        type={clickable ? 'button' : undefined}
                        onClick={clickable ? () => jumpToStep(s.key) : undefined}
                        className={`flex flex-col items-center min-w-[4.25rem] bg-transparent border-0 p-0 ${
                          clickable ? 'cursor-pointer group' : ''
                        }`}
                        aria-label={clickable ? `Revenir à ${s.label}` : s.label}
                      >
                        <span
                          className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-all duration-300 ${
                            done
                              ? 'bg-[#3a00e0] border-[#3a00e0] text-white group-hover:bg-[#5b21ff]'
                              : active
                              ? 'bg-white border-[#3a00e0] text-[#3a00e0] shadow-[0_0_0_4px_rgba(58,0,224,0.12)]'
                              : 'bg-white border-slate-300 text-slate-400'
                          }`}
                        >
                          {done ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : n}
                        </span>
                        <span
                          className={`mt-2 text-[11px] leading-tight text-center max-w-[4.5rem] transition-colors ${
                            active ? 'text-[#3a00e0] font-semibold' : done ? 'text-slate-600 group-hover:text-[#3a00e0]' : 'text-slate-400'
                          }`}
                        >
                          {s.label}
                        </span>
                      </StepInner>
                      {i < steps.length - 1 && (
                        <div
                          className={`mt-[1.125rem] mx-1.5 h-0.5 flex-1 rounded-full transition-colors duration-500 ${
                            isStepComplete(s.key) ? 'bg-gradient-to-r from-[#3a00e0] to-[#fab03a]' : 'bg-slate-200'
                          }`}
                          aria-hidden
                        />
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>

          {/* STEP 0 : Contact / lead */}
          {step === 0 && (
            <StepPanel stepKey="contact">
              <StepShell
                title="Votre email"
                description="Pour vous recontacter. Ensuite : activité, créneau et paiement."
                icon={User}
              >
                {(selectedOffer || selectedFormula) && (
                  <div className="mb-5 rounded-xl bg-[rgba(58,0,224,0.04)] border border-[rgba(58,0,224,0.12)] px-4 py-3 text-sm">
                    <span className="text-slate-500">Sélection : </span>
                    <span className="text-slate-900 font-medium">
                      {[selectedOffer?.title, selectedFormula?.name].filter(Boolean).join(' — ')}
                    </span>
                  </div>
                )}
                <form onSubmit={handleCreateLead} className="space-y-5" noValidate>
                  <div className="space-y-2">
                    <Label htmlFor="lead_email" className="text-slate-700">Email *</Label>
                    <Input
                      id="lead_email"
                      type="email"
                      autoComplete="email"
                      value={leadEmail}
                      onChange={(e) => setLeadEmail(e.target.value)}
                      onBlur={() => setLeadTouched((p) => ({ ...p, email: true }))}
                      className={`${inputClass} ${leadTouched.email && leadEmailError ? inputErrorClass : ''}`}
                      placeholder="votre@email.com"
                      aria-invalid={leadTouched.email && !!leadEmailError}
                    />
                    {leadTouched.email && <FieldError message={leadEmailError} />}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lead_name" className="text-slate-700">Nom (optionnel)</Label>
                    <Input
                      id="lead_name"
                      autoComplete="name"
                      value={leadName}
                      onChange={(e) => setLeadName(e.target.value)}
                      className={inputClass}
                      placeholder="Votre nom"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || !isLeadValid}
                    className={`w-full h-11 ${BTN_PRIMARY} disabled:opacity-50`}
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enregistrement…</>
                    ) : selectedOffer ? (
                      <>Continuer la réservation<ArrowRight className="ml-2 h-4 w-4" /></>
                    ) : (
                      <>Voir les activités<ArrowRight className="ml-2 h-4 w-4" /></>
                    )}
                  </Button>
                </form>
              </StepShell>
            </StepPanel>
          )}

          {/* STEP 1 : Offre */}
          {step === 1 && (
            <StepPanel stepKey="offer">
              <StepShell
                title="Choisissez votre activité"
                description="Ateliers, cursus et stages. Les cours particuliers se font sur rendez-vous : un conseiller vous rappelle sous 24h."
                icon={Package}
                footer={(
                  <Button variant="outline" className={backBtnClass} onClick={() => goToStep(0)}>
                    <ChevronLeft className="h-4 w-4 mr-1" />Modifier l&apos;email
                  </Button>
                )}
              >
                {offersLoading ? (
                  <LoadingBlock message="Chargement des offres…" />
                ) : filteredOffers.length === 0 ? (
                  <div className="text-center py-10 space-y-2">
                    <AlertCircle className="h-8 w-8 text-orange-400 mx-auto" aria-hidden />
                    <p className="text-slate-800 font-medium">Aucune offre disponible</p>
                    <p className="text-sm text-slate-500">Réessayez plus tard ou contactez-nous.</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3" role="list">
                    {filteredOffers.map((offer) => {
                      const selected = selectedOffer?.id === offer.id;
                      const free = isFreeOffer(offer);
                      const badge = offer.promo_badge || (free ? 'OFFERT' : null);
                      return (
                        <button
                          key={offer.id}
                          type="button"
                          role="listitem"
                          aria-pressed={selected}
                          className={`text-left rounded-xl border p-4 sm:p-5 ${selectableCardClass} ${selected ? selectedCardClass : ''}`}
                          onClick={() => handleSelectOffer(offer)}
                        >
                          <div className="flex items-start justify-between mb-2 gap-2">
                            <div>
                              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">
                                {categoryLabel(offer.category)}
                              </p>
                              <h3 className="text-slate-900 font-semibold text-sm sm:text-base">{offer.title}</h3>
                            </div>
                            {badge && (
                              <Badge className={`shrink-0 text-xs ${
                                free
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : 'bg-[rgba(250,176,58,0.18)] text-[#c47a0a]'
                              }`}
                              >
                                {badge}
                              </Badge>
                            )}
                          </div>
                          <p className="text-slate-600 text-sm line-clamp-2 leading-relaxed">
                            {offer.short_description || offer.description}
                          </p>
                          <div className="mt-3 flex items-end justify-between gap-2">
                            <p className={`text-sm font-semibold tabular-nums ${free ? 'text-emerald-700' : 'text-[#3a00e0]'}`}>
                              {offerPriceLabel(offer)}
                            </p>
                            <p className="text-slate-400 text-xs">
                              {offer.formulas?.length || 0} formule{(offer.formulas?.length || 0) > 1 ? 's' : ''}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </StepShell>
            </StepPanel>
          )}

          {/* STEP 2 : Formule */}
          {step === 2 && (
            <StepPanel stepKey="formula">
              {!selectedOffer ? (
                <EmptyStep
                  message="Choisissez d'abord une offre."
                  hint="Revenez à l'étape précédente pour sélectionner une activité."
                  onBack={() => goToStep(1)}
                />
              ) : (
                <StepShell
                  title="Choisissez votre formule"
                  description={selectedOffer.title}
                  icon={Tag}
                  footer={(
                    <Button variant="outline" className={backBtnClass} onClick={() => goToStep(1)}>
                      <ChevronLeft className="h-4 w-4 mr-1" />Changer d&apos;activité
                    </Button>
                  )}
                >
                  {(selectedOffer.formulas || []).length === 0 ? (
                    <div className="text-center py-10 space-y-2">
                      <p className="text-slate-800 font-medium">Aucune formule pour cette offre</p>
                      <p className="text-sm text-slate-500">Essayez une autre offre ou contactez-nous.</p>
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3" role="list">
                      {(selectedOffer.formulas || []).map((formula) => {
                        const selected = selectedFormula?.id === formula.id;
                        return (
                          <button
                            key={formula.id}
                            type="button"
                            role="listitem"
                            aria-pressed={selected}
                            className={`text-left rounded-xl border p-4 sm:p-5 ${selectableCardClass} ${selected ? selectedCardClass : ''}`}
                            onClick={() => handleSelectFormula(formula)}
                          >
                            <div className="flex items-center justify-between mb-1 gap-2">
                              <h3 className="text-slate-900 font-semibold text-sm sm:text-base">{formula.name}</h3>
                              {formula.badge && (
                                <Badge className="bg-[rgba(58,0,224,0.1)] text-[#3a00e0] text-xs shrink-0">{formula.badge}</Badge>
                              )}
                            </div>
                            {formula.description && (
                              <p className="text-slate-600 text-sm mb-3 line-clamp-2 leading-relaxed">{formula.description}</p>
                            )}
                            <div className="text-xl sm:text-2xl font-bold text-slate-900 tabular-nums">
                              {formula.price === 0 ? 'Gratuit' : `${formula.price} €`}
                            </div>
                            {formula.price_per_month && (
                              <div className="text-sm text-slate-500 mt-0.5">ou {formula.price_per_month} €/mois</div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </StepShell>
              )}
            </StepPanel>
          )}

          {/* STEP 3 : Ville */}
          {step === 3 && centerStepEnabled && (
            <StepPanel stepKey="city">
              {!selectedFormula ? (
                <EmptyStep
                  message="Choisissez d'abord une formule."
                  onBack={() => goToStep(2)}
                />
              ) : (
                <StepShell
                  title="Choisissez votre centre"
                  description={`${selectedOffer?.title} — ${selectedFormula.name}`}
                  icon={MapPin}
                  footer={(
                    <Button variant="outline" className={backBtnClass} onClick={backFromCity}>
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      {skippedFormulaStep || (selectedOffer?.formulas || []).length <= 1
                        ? "Changer d'activité"
                        : 'Changer de formule'}
                    </Button>
                  )}
                >
                  {schedulesLoading ? (
                    <LoadingBlock message="Chargement des centres…" />
                  ) : eligibleCenters.length === 0 ? (
                    <div className="text-center py-10 space-y-2">
                      <AlertCircle className="h-8 w-8 text-orange-400 mx-auto" aria-hidden />
                      <p className="text-slate-800 font-medium">Aucun centre pour cette formule</p>
                      <p className="text-sm text-slate-500">Essayez une autre formule ou contactez-nous.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="list">
                      {eligibleCenters.map((center) => {
                        const spots = openSchedules
                          .filter((s) => s.center_id === center.id)
                          .reduce((sum, s) => sum + (s.available_spots || 0), 0);
                        const selected = selectedCenterId === center.id;
                        return (
                          <button
                            key={center.id}
                            type="button"
                            role="listitem"
                            aria-pressed={selected}
                            className={`text-left rounded-xl border p-4 flex items-center gap-3 ${selectableCardClass} ${selected ? selectedCardClass : ''}`}
                            onClick={() => handleSelectCenter(center.id)}
                          >
                            <div className="w-10 h-10 rounded-xl bg-[rgba(58,0,224,0.08)] flex items-center justify-center shrink-0">
                              <MapPin className="h-5 w-5 text-[#3a00e0]" aria-hidden />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-slate-900 font-semibold">{center.name}</h3>
                              <p className={`text-xs mt-0.5 ${spots > 0 ? 'text-slate-500' : 'text-red-500'}`}>
                                {spots > 0 ? `${spots} place${spots > 1 ? 's' : ''} disponible${spots > 1 ? 's' : ''}` : 'Complet'}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </StepShell>
              )}
            </StepPanel>
          )}

          {/* STEP 4 : Jour */}
          {step === 4 && (
            <StepPanel stepKey="day">
              {centerStepEnabled && !selectedCenterId ? (
                <EmptyStep message="Choisissez d'abord une ville." onBack={() => goToStep(3)} />
              ) : (
                <StepShell
                  title="Choisissez votre créneau"
                  description={scheduleHint}
                  icon={Clock}
                  footer={(
                    <Button
                      variant="outline"
                      className={backBtnClass}
                      onClick={() => goToStep(centerStepEnabled ? 3 : (skippedFormulaStep ? 1 : 2))}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      {centerStepEnabled
                        ? 'Changer de centre'
                        : skippedFormulaStep || (selectedOffer?.formulas || []).length <= 1
                          ? "Changer d'activité"
                          : 'Changer de formule'}
                    </Button>
                  )}
                >
                  {schedulesLoading ? (
                    <LoadingBlock message="Chargement des créneaux…" />
                  ) : centerSchedules.length === 0 ? (
                    <div className="text-center py-10 space-y-4">
                      <AlertCircle className="h-8 w-8 text-orange-400 mx-auto" aria-hidden />
                      <div className="space-y-2">
                        <p className="text-slate-800 font-medium">
                          {openSchedules.length === 0 && selectedFormula?.name
                            ? `Aucun créneau disponible pour la formule ${selectedFormula.name}`
                            : 'Aucun créneau disponible'}
                        </p>
                        <p className="text-sm text-slate-500">
                          {centerStepEnabled && openSchedules.length > 0
                            ? 'Revenez choisir une autre ville.'
                            : altFormulas.length > 0
                              ? 'Choisissez une autre formule de cette offre qui a des créneaux :'
                              : 'Essayez une autre formule ou contactez-nous.'}
                        </p>
                      </div>
                      {openSchedules.length === 0 && (altFormulasLoading || altFormulas.length > 0) ? (
                        <div className="flex flex-wrap justify-center gap-2 pt-1">
                          {altFormulasLoading && altFormulas.length === 0 ? (
                            <span className="text-sm text-slate-400 inline-flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                              Recherche d&apos;autres formules…
                            </span>
                          ) : null}
                          {altFormulas.map((formula) => (
                            <Button
                              key={formula.id}
                              type="button"
                              variant="outline"
                              className={BTN_OUTLINE}
                              onClick={() => handleSelectFormula(formula)}
                            >
                              Voir {formula.name}
                              <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                            </Button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="space-y-2.5" role="list">
                      {centerSchedules.map((schedule) => {
                        const isFull = schedule.available_spots <= 0;
                        const isClosed = !!schedule.closed;
                        const disabled = !isValidSlot(schedule, selectedOffer);
                        const dateLabel = slotDateLabel(schedule);
                        const selected = slotOptionKey(selectedSchedule) === slotOptionKey(schedule);
                        return (
                          <button
                            key={slotOptionKey(schedule)}
                            type="button"
                            role="listitem"
                            disabled={disabled}
                            aria-pressed={selected}
                            aria-disabled={disabled}
                            className={`w-full rounded-xl border px-4 py-3.5 flex items-center justify-between gap-3 text-left transition-all ${
                              disabled
                                ? 'opacity-55 cursor-not-allowed border-slate-200 bg-slate-50'
                                : `${selectableCardClass} ${selected ? selectedCardClass : ''}`
                            }`}
                            onClick={() => !disabled && handleSelectSchedule(schedule)}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[rgba(58,0,224,0.08)] flex items-center justify-center shrink-0">
                                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-[#3a00e0]" aria-hidden />
                              </div>
                              <div className="min-w-0">
                                <div className="text-slate-900 font-semibold text-sm sm:text-base truncate">
                                  {schedule.center_name || 'Centre à confirmer'}
                                </div>
                                <div className="text-slate-700 capitalize text-sm tabular-nums">
                                  {dateLabel || slotDaysLabel(schedule)}
                                  {' · '}
                                  {schedule.start_time} — {schedule.end_time}
                                </div>
                                <div className="text-slate-500 text-xs sm:text-sm truncate">
                                  {schedule.center_address ? `${schedule.center_address}` : null}
                                  {dateLabel ? (
                                    <span className="capitalize">{schedule.center_address ? ' · ' : ''}{slotDaysLabel(schedule)}</span>
                                  ) : null}
                                  {schedule.stage_name ? ` · ${schedule.stage_name}` : ''}
                                </div>
                              </div>
                            </div>
                            <Badge className={`shrink-0 text-xs ${
                              isClosed
                                ? 'bg-slate-500/10 text-slate-500 border-slate-500/20'
                                : schedule.available_spots > 5
                                ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
                                : schedule.available_spots > 0
                                ? 'bg-orange-500/10 text-orange-700 border-orange-500/20'
                                : 'bg-red-500/10 text-red-600 border-red-500/20'
                            }`}>
                              {isClosed
                                ? 'Fermé'
                                : schedule.available_spots > 0
                                ? `${schedule.available_spots} places`
                                : 'Complet'}
                            </Badge>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </StepShell>
              )}
            </StepPanel>
          )}

          {/* STEP 5 : Infos */}
          {step === 5 && (
            <StepPanel stepKey="info">
              {!appointmentOffer && !isValidSlot(selectedSchedule, selectedOffer) ? (
                <EmptyStep message="Choisissez d'abord un créneau." onBack={() => goToStep(4)} />
              ) : (
                <StepShell
                  title="Parent et enfant"
                  description={
                    appointmentOffer
                      ? 'Cours particulier sur rendez-vous. Un conseiller vous recontacte sous 24h pour convenir d’un horaire.'
                      : (
                        <>
                          {selectedSchedule.center_name}
                          {slotDateLabel(selectedSchedule) ? ` · ${slotDateLabel(selectedSchedule)}` : null}
                          {' · '}
                          <span className="capitalize">{slotDaysLabel(selectedSchedule)}</span>{' '}
                          <span className="tabular-nums">{selectedSchedule.start_time} — {selectedSchedule.end_time}</span>
                        </>
                      )
                  }
                  icon={User}
                >
                  <form onSubmit={handleCreateBooking} className="space-y-7" noValidate>
                    <fieldset className="space-y-4">
                      <legend className="text-slate-900 font-semibold flex items-center gap-2 mb-1">
                        <User className="h-4 w-4 text-[#3a00e0]" aria-hidden />
                        Parent / tuteur
                      </legend>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2 sm:col-span-2">
                          <Label htmlFor="parent_name" className="text-slate-700">Nom complet *</Label>
                          <Input
                            id="parent_name"
                            name="parent_name"
                            autoComplete="name"
                            value={formData.parent_name}
                            onChange={handleFormChange}
                            onBlur={() => setInfoTouched((p) => ({ ...p, parent_name: true }))}
                            className={infoFieldClass('parent_name')}
                            placeholder="Votre nom"
                            aria-invalid={infoTouched.parent_name && !!infoErrors.parent_name}
                          />
                          {infoTouched.parent_name && <FieldError message={infoErrors.parent_name} />}
                        </div>
                        {showEmailField ? (
                          <div className="space-y-2">
                            <Label htmlFor="parent_email" className="text-slate-700">Email *</Label>
                            <Input
                              id="parent_email"
                              name="parent_email"
                              type="email"
                              autoComplete="email"
                              value={formData.parent_email}
                              onChange={handleFormChange}
                              onBlur={() => setInfoTouched((p) => ({ ...p, parent_email: true }))}
                              className={infoFieldClass('parent_email')}
                              placeholder="votre@email.com"
                              aria-invalid={infoTouched.parent_email && !!infoErrors.parent_email}
                            />
                            {infoTouched.parent_email && <FieldError message={infoErrors.parent_email} />}
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Email</p>
                            <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                              <span className="text-sm text-slate-900 truncate">{formData.parent_email}</span>
                              <button
                                type="button"
                                onClick={() => setUnlockEmail(true)}
                                className="text-xs font-medium text-[#3a00e0] hover:underline shrink-0"
                              >
                                Modifier
                              </button>
                            </div>
                            <p className="text-xs text-slate-500">Saisi à l&apos;étape email.</p>
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label htmlFor="parent_phone" className="text-slate-700">Téléphone *</Label>
                          <Input
                            id="parent_phone"
                            name="parent_phone"
                            type="tel"
                            autoComplete="tel"
                            value={formData.parent_phone}
                            onChange={handleFormChange}
                            onBlur={() => setInfoTouched((p) => ({ ...p, parent_phone: true }))}
                            className={infoFieldClass('parent_phone')}
                            placeholder="06 12 34 56 78"
                            aria-invalid={infoTouched.parent_phone && !!infoErrors.parent_phone}
                          />
                          {infoTouched.parent_phone && <FieldError message={infoErrors.parent_phone} />}
                        </div>
                      </div>
                    </fieldset>
                    <fieldset className="space-y-4">
                      <legend className="text-slate-900 font-semibold flex items-center gap-2 mb-1">
                        <Baby className="h-4 w-4 text-[#3a00e0]" aria-hidden />
                        Enfant
                      </legend>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="child_name" className="text-slate-700">Prénom *</Label>
                          <Input
                            id="child_name"
                            name="child_name"
                            value={formData.child_name}
                            onChange={handleFormChange}
                            onBlur={() => setInfoTouched((p) => ({ ...p, child_name: true }))}
                            className={infoFieldClass('child_name')}
                            placeholder="Prénom"
                            aria-invalid={infoTouched.child_name && !!infoErrors.child_name}
                          />
                          {infoTouched.child_name && <FieldError message={infoErrors.child_name} />}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="child_age" className="text-slate-700">Âge (6–16) *</Label>
                          <Input
                            id="child_age"
                            name="child_age"
                            type="number"
                            min="6"
                            max="16"
                            inputMode="numeric"
                            value={formData.child_age}
                            onChange={handleFormChange}
                            onBlur={() => setInfoTouched((p) => ({ ...p, child_age: true }))}
                            className={infoFieldClass('child_age')}
                            placeholder="Âge"
                            aria-invalid={infoTouched.child_age && !!infoErrors.child_age}
                          />
                          {infoTouched.child_age && <FieldError message={infoErrors.child_age} />}
                        </div>
                      </div>
                    </fieldset>
                    <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => goToStep(appointmentOffer ? (skippedFormulaStep ? 1 : 2) : 4)}
                        className={`flex-1 h-11 ${backBtnClass}`}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        {appointmentOffer
                          ? (skippedFormulaStep || (selectedOffer?.formulas || []).length <= 1
                            ? "Changer d'activité"
                            : 'Changer de formule')
                          : 'Changer de créneau'}
                      </Button>
                      <Button
                        type="submit"
                        disabled={loading || !isInfoValid}
                        className={`flex-1 h-11 ${BTN_PRIMARY} disabled:opacity-50`}
                      >
                        {loading ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Traitement…</>
                        ) : appointmentOffer ? (
                          <>Envoyer la demande<ArrowRight className="ml-2 h-4 w-4" /></>
                        ) : (
                          <>Voir le récapitulatif<ArrowRight className="ml-2 h-4 w-4" /></>
                        )}
                      </Button>
                    </div>
                  </form>
                </StepShell>
              )}
            </StepPanel>
          )}

          {/* STEP 6 : Recap + promo + payment */}
          {step === 6 && (
            <StepPanel stepKey="recap">
              {!booking ? (
                <EmptyStep
                  message="Réservation introuvable."
                  hint="Reprenez depuis le début pour créer une nouvelle inscription."
                  onBack={() => goToStep(0)}
                  backLabel="Recommencer"
                />
              ) : (
                <StepShell
                  title={appointmentOffer ? 'Demande envoyée' : 'Récapitulatif'}
                  description={
                    appointmentOffer
                      ? 'Un conseiller vous recontacte sous 24h pour convenir d’un rendez-vous.'
                      : paymentsEnabled
                      ? 'Vérifiez les détails, appliquez un code promo si besoin, puis payez.'
                      : 'Vérifiez les détails et confirmez. Le règlement se fera sur place, le paiement en ligne n’est pas encore activé.'
                  }
                  icon={CheckCircle2}
                >
                  {appointmentOffer ? (
                    <div className="space-y-5">
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-4 text-sm text-emerald-900 leading-relaxed">
                        Votre demande de cours particulier est bien enregistrée. Nous vous appelons
                        ou vous écrivons sous 24h pour caler le créneau avec un formateur.
                      </div>
                      <dl className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                        {[
                          { label: 'Offre', value: selectedOffer?.title },
                          { label: 'Formule', value: selectedFormula?.name },
                          { label: 'Parent', value: formData.parent_name },
                          { label: 'Téléphone', value: formData.parent_phone },
                          { label: 'Enfant', value: formData.child_name ? `${formData.child_name} (${formData.child_age} ans)` : null },
                        ].map(({ label, value }) => (
                          <div key={label} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-0.5 px-4 py-3 bg-white">
                            <dt className="text-slate-500 text-sm">{label}</dt>
                            <dd className="text-slate-900 font-medium text-sm sm:text-base sm:text-right">{value || '—'}</dd>
                          </div>
                        ))}
                      </dl>
                      <Link to="/" className="block">
                        <Button className={`w-full h-11 ${BTN_PRIMARY}`}>
                          Retour à l&apos;accueil
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  ) : (
                  <>
                  <div className="space-y-5">
                    <dl className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                      {[
                        { label: 'Offre', value: selectedOffer?.title },
                        { label: 'Formule', value: selectedFormula?.name },
                        { label: 'Centre', value: selectedSchedule?.center_name },
                        {
                          label: 'Date',
                          value: slotDateLabel(selectedSchedule) || null,
                        },
                        {
                          label: 'Créneau',
                          value: selectedSchedule
                            ? `${slotDaysLabel(selectedSchedule)} · ${selectedSchedule.start_time} — ${selectedSchedule.end_time}`
                            : null,
                        },
                        { label: 'Parent', value: formData.parent_name },
                        { label: 'Enfant', value: formData.child_name ? `${formData.child_name} (${formData.child_age} ans)` : null },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-0.5 px-4 py-3 bg-white">
                          <dt className="text-slate-500 text-sm">{label}</dt>
                          <dd className="text-slate-900 font-medium text-sm sm:text-base capitalize sm:text-right">{value || '—'}</dd>
                        </div>
                      ))}
                    </dl>

                    <div className="rounded-xl border border-slate-200 p-4">
                      <Label htmlFor="promo_code" className="text-slate-700 flex items-center mb-2.5 text-sm font-medium">
                        <Tag className="h-4 w-4 mr-2 text-[#3a00e0]" aria-hidden />
                        Code promo
                      </Label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          id="promo_code"
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                          placeholder="Ex. BIENVENUE"
                          className={inputClass}
                          disabled={promoLoading}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleApplyPromo();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleApplyPromo}
                          disabled={!promoInput.trim() || promoLoading}
                          className={`${BTN_OUTLINE} shrink-0 disabled:opacity-50`}
                        >
                          {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Appliquer'}
                        </Button>
                      </div>
                      {promoInfo && (
                        <p className="text-emerald-700 text-sm mt-2.5 flex items-center gap-1.5" role="status">
                          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          Code {promoInfo.code} appliqué : −{promoInfo.discount} €
                        </p>
                      )}
                    </div>

                    <div className="rounded-xl border border-[rgba(58,0,224,0.15)] bg-[rgba(58,0,224,0.03)] p-4 space-y-2">
                      {displayDiscount > 0 && (
                        <>
                          <div className="flex justify-between text-sm text-slate-600 tabular-nums">
                            <span>Sous-total</span>
                            <span>{displayOriginal} €</span>
                          </div>
                          <div className="flex justify-between text-sm text-emerald-700 tabular-nums">
                            <span>Réduction</span>
                            <span>−{displayDiscount} €</span>
                          </div>
                        </>
                      )}
                      <div className={`flex justify-between items-baseline ${displayDiscount > 0 ? 'pt-2 border-t border-[rgba(58,0,224,0.12)]' : ''}`}>
                        <span className="text-base text-slate-900 font-semibold">
                          {paymentsEnabled ? 'Total à régler' : 'Montant'}
                        </span>
                        <span className="text-2xl sm:text-3xl font-bold text-[#3a00e0] tabular-nums">
                          {displayAmount === 0 ? 'Gratuit' : `${displayAmount} €`}
                        </span>
                      </div>
                    </div>

                    <div className="hidden sm:flex flex-row gap-3">
                      <Button type="button" variant="outline" onClick={() => goToStep(5)} className={`flex-1 h-11 ${backBtnClass}`}>
                        <ChevronLeft className="h-4 w-4 mr-1" />Modifier
                      </Button>
                      <Button
                        onClick={handleCheckout}
                        disabled={paymentLoading}
                        className={`flex-[1.4] h-11 ${BTN_PRIMARY} disabled:opacity-50`}
                      >
                        {paymentLoading ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{paymentsEnabled && displayAmount > 0 ? 'Redirection…' : 'Confirmation…'}</>
                        ) : paymentsEnabled && displayAmount > 0 ? (
                          <><CreditCard className="mr-2 h-4 w-4" />Passer au paiement</>
                        ) : (
                          <><CheckCircle2 className="mr-2 h-4 w-4" />Confirmer la demande</>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Sticky CTA mobile */}
                  <div
                    className="sm:hidden fixed bottom-0 inset-x-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur-md px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.18)]"
                  >
                    <div className="flex items-center justify-between gap-3 mb-2.5">
                      <span className="text-sm text-slate-500">Total</span>
                      <span className="text-lg font-bold text-[#3a00e0] tabular-nums">
                        {displayAmount === 0 ? 'Gratuit' : `${displayAmount} €`}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" onClick={() => goToStep(5)} className={`shrink-0 ${backBtnClass}`}>
                        Modifier
                      </Button>
                      <Button
                        onClick={handleCheckout}
                        disabled={paymentLoading}
                        className={`flex-1 h-11 ${BTN_PRIMARY} disabled:opacity-50`}
                      >
                        {paymentLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : paymentsEnabled && displayAmount > 0 ? (
                          <><CreditCard className="mr-2 h-4 w-4" />Payer</>
                        ) : (
                          <><CheckCircle2 className="mr-2 h-4 w-4" />Confirmer</>
                        )}
                      </Button>
                    </div>
                  </div>
                  </>
                  )}
                </StepShell>
              )}
            </StepPanel>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Reserver;
